import React, { useState } from 'react';
import { X, Landmark, AlertTriangle, Pencil, Send, CheckCircle2, Ban } from 'lucide-react';
import { DialogShell } from './ui/DialogShell';
import { Textarea } from './ui/Textarea';
import { Checkbox } from './ui/Checkbox';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { DeudaModal } from './DeudaModal';
import { PagoDeudaModal } from './PagoDeudaModal';
import { ComprobanteSoloLectura } from './comprobantes/ComprobanteSoloLectura';
import { deudaService } from '../services/deudaService';
import { DeudaConEstado, DeudaPago, EstadoDeuda } from '../lib/supabase';
import { desglosarCuotas, CuotaDesglosada, EstadoCuota } from '../utils/deudaCalculations';
import { formatCurrency, formatDate } from '../utils/formatters';

interface DeudaDetalleModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly deuda: DeudaConEstado;
  readonly soloLectura: boolean;
  readonly onChanged: () => void;
}

const ESTADO_DEUDA_LABEL: Record<EstadoDeuda, string> = { activa: 'Activa', saldada: 'Saldada', anulada: 'Anulada' };
const ESTADO_DEUDA_CLASS: Record<EstadoDeuda, string> = {
  activa: 'bg-blue-100 text-blue-700',
  saldada: 'bg-emerald-100 text-emerald-700',
  anulada: 'bg-slate-200 text-slate-500',
};

const ESTADO_CUOTA_LABEL: Record<EstadoCuota, string> = { pagada: 'Pagada', parcial: 'Parcial', pendiente: 'Pendiente', atrasada: 'Atrasada' };
const ESTADO_CUOTA_CLASS: Record<EstadoCuota, string> = {
  pagada: 'bg-emerald-100 text-emerald-700',
  parcial: 'bg-amber-100 text-amber-700',
  pendiente: 'bg-slate-100 text-slate-600',
  atrasada: 'bg-red-100 text-red-700',
};

const FORMATO_FECHA_CORTA: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };

// ---------------------------------------------------------------------------
// Dialogo de anulación con motivo obligatorio (privado a este módulo:
// DangerConfirmDialog no soporta campo de texto libre).
// ---------------------------------------------------------------------------

interface AnularConMotivoDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (motivo: string) => Promise<void>;
  readonly titulo: string;
  readonly mensaje: string;
  readonly confirmText: string;
}

const AnularConMotivoDialog: React.FC<AnularConMotivoDialogProps> = ({ isOpen, onClose, onConfirm, titulo, mensaje, confirmText }) => {
  const [motivo, setMotivo] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (procesando) return;
    setMotivo('');
    setIsChecked(false);
    setError('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!motivo.trim()) { setError('Indica el motivo de la anulación'); return; }
    if (!isChecked) return;
    try {
      setProcesando(true);
      await onConfirm(motivo.trim());
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al anular. Intenta nuevamente.');
    } finally {
      setProcesando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      level="nested"
      showCloseButton={false}
      header={(
        <div className="bg-red-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500 rounded-lg"><AlertTriangle className="w-6 h-6" /></div>
            <h3 className="text-lg font-bold">{titulo}</h3>
          </div>
          <button type="button" onClick={handleClose} disabled={procesando} className="text-white/80 hover:text-white transition-colors disabled:opacity-50" aria-label="Cerrar">
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    >
      <div className="space-y-4">
        <p className="text-slate-700">{mensaje}</p>
        <Textarea label="Motivo de la anulación" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Explica por qué se anula..." required rows={2} />
        <FormErrorAlert error={error || null} onClear={() => { setError(''); }} />
        <div className="p-3 bg-slate-100 rounded-lg">
          <Checkbox label="Entiendo que esta acción no se puede deshacer" checked={isChecked} onChange={setIsChecked} />
        </div>
        <div className="flex gap-3 justify-end pt-2">
          <button type="button" onClick={handleClose} disabled={procesando} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={procesando || !isChecked}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${isChecked ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'}`}
          >
            {procesando ? 'Anulando...' : confirmText}
          </button>
        </div>
      </div>
    </DialogShell>
  );
};

// ---------------------------------------------------------------------------
// Resumen
// ---------------------------------------------------------------------------

interface ResumenDeudaProps {
  readonly deuda: DeudaConEstado;
}

const ResumenDeuda: React.FC<ResumenDeudaProps> = ({ deuda }) => (
  <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${ESTADO_DEUDA_CLASS[deuda.estado]}`}>
        {ESTADO_DEUDA_LABEL[deuda.estado]}
      </span>
      {deuda.cuotasAtrasadas > 0 && (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
          {deuda.cuotasAtrasadas} {deuda.cuotasAtrasadas === 1 ? 'cuota atrasada' : 'cuotas atrasadas'}
        </span>
      )}
      <span className="text-xs text-slate-400 ml-auto">Crédito desde {formatDate(deuda.fecha_inicio_credito, FORMATO_FECHA_CORTA)}</span>
    </div>
    <div className="grid grid-cols-3 gap-3 text-sm">
      <div>
        <p className="text-slate-500">Progreso</p>
        <p className="font-semibold text-slate-800">{deuda.cuotasPagadas} de {deuda.numero_cuotas} cuotas</p>
      </div>
      <div>
        <p className="text-slate-500">Pagado</p>
        <p className="font-semibold text-emerald-700">{formatCurrency(deuda.pagado)}</p>
      </div>
      <div>
        <p className="text-slate-500">Saldo</p>
        <p className="font-semibold text-slate-800">{formatCurrency(deuda.saldo)}</p>
      </div>
    </div>
    <div className="flex items-center gap-2 flex-wrap text-sm">
      <span className="text-slate-500">Comprobante de origen</span>
      <ComprobanteSoloLectura
        etiqueta="Comprobante de origen"
        comprobante={deuda.comprobante_origen ?? null}
        textoSinComprobante="Sin comprobante de origen en marcha blanca"
      />
    </div>
    {deuda.movimiento_financiamiento_id && (
      <p className="text-xs text-slate-500">La entrada por financiamiento se registró con la fecha de alta de la deuda en Chile.</p>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Cronología
// ---------------------------------------------------------------------------

interface FilaCuotaProps {
  readonly cuota: CuotaDesglosada;
  readonly soloLectura: boolean;
  readonly onAnularPago: (pago: DeudaPago) => void;
}

function badgePago(pago: DeudaPago, cuota: CuotaDesglosada): React.ReactNode {
  if (pago.es_historia_previa) {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-600">historia previa</span>;
  }
  if (cuota.estado === 'parcial') {
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">abono parcial</span>;
  }
  return null;
}

const FilaCuota: React.FC<FilaCuotaProps> = ({ cuota, soloLectura, onAnularPago }) => (
  <div className="border border-slate-200 rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-4 py-2.5 bg-white">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-700">Cuota {cuota.numeroCuota}</span>
        <span className="text-xs text-slate-400">vence {formatDate(cuota.vencimiento, FORMATO_FECHA_CORTA)}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-600">{formatCurrency(cuota.montoEsperado)}</span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ESTADO_CUOTA_CLASS[cuota.estado]}`}>
          {ESTADO_CUOTA_LABEL[cuota.estado]}
        </span>
      </div>
    </div>
    {cuota.pagos.length > 0 && (
      <div className="divide-y divide-slate-100 bg-slate-50">
        {cuota.pagos.map((pago) => (
          <div key={pago.id} className="flex items-center justify-between px-4 py-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-slate-500">{formatDate(pago.fecha, FORMATO_FECHA_CORTA)}</span>
              <span className="text-slate-700 font-medium">{formatCurrency(pago.monto)}</span>
              {badgePago(pago, cuota)}
            </div>
            {!soloLectura && (
              <button
                type="button"
                onClick={() => onAnularPago(pago)}
                className="text-xs text-red-600 hover:text-red-800 font-medium"
              >
                Anular pago
              </button>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export const DeudaDetalleModal: React.FC<DeudaDetalleModalProps> = ({ isOpen, onClose, deuda, soloLectura, onChanged }) => {
  const [accionModal, setAccionModal] = useState<'pago' | 'saldar' | 'editar' | null>(null);
  const [anulandoDeuda, setAnulandoDeuda] = useState(false);
  const [pagoAnulando, setPagoAnulando] = useState<DeudaPago | null>(null);

  if (!isOpen) return null;

  const cuotas = desglosarCuotas(deuda, deuda.pagos);
  const puedeGestionar = !soloLectura && deuda.estado === 'activa';

  const handleAccionExitosa = () => {
    setAccionModal(null);
    onChanged();
  };

  const handleAnularDeuda = async (motivo: string) => {
    await deudaService.anularDeuda(deuda.id, motivo);
    setAnulandoDeuda(false);
    onChanged();
  };

  const handleAnularPago = async (motivo: string) => {
    if (!pagoAnulando) return;
    await deudaService.anularPago(pagoAnulando, motivo);
    setPagoAnulando(null);
    onChanged();
  };

  return (
    <>
      <DialogShell
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        header={(
          <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Landmark className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800">{deuda.acreedor}</h2>
                {deuda.descripcion && <p className="text-sm text-slate-500">{deuda.descripcion}</p>}
              </div>
            </div>
            <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" aria-label="Cerrar">
              <X className="w-6 h-6 text-slate-600" />
            </button>
          </div>
        )}
      >
        <div className="space-y-6">
          <ResumenDeuda deuda={deuda} />

          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Cronología de pagos</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {cuotas.map((cuota) => (
                <FilaCuota key={cuota.numeroCuota} cuota={cuota} soloLectura={soloLectura} onAnularPago={setPagoAnulando} />
              ))}
            </div>
          </div>

          {puedeGestionar && (
            <div className="flex flex-wrap gap-3 pt-2 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setAccionModal('pago')}
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Send className="w-4 h-4" /> Registrar pago
              </button>
              <button
                type="button"
                onClick={() => setAccionModal('saldar')}
                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" /> Saldar deuda
              </button>
              <button
                type="button"
                onClick={() => setAccionModal('editar')}
                className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-200 transition-colors"
              >
                <Pencil className="w-4 h-4" /> Editar
              </button>
              <button
                type="button"
                onClick={() => setAnulandoDeuda(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-red-50 text-red-700 text-sm font-semibold rounded-lg hover:bg-red-100 transition-colors ml-auto"
              >
                <Ban className="w-4 h-4" /> Anular deuda
              </button>
            </div>
          )}
        </div>
      </DialogShell>

      {(accionModal === 'pago' || accionModal === 'saldar') && (
        <PagoDeudaModal
          isOpen
          onClose={() => setAccionModal(null)}
          onSuccess={handleAccionExitosa}
          deuda={deuda}
          modo={accionModal}
        />
      )}

      {accionModal === 'editar' && (
        <DeudaModal
          isOpen
          onClose={() => setAccionModal(null)}
          onSuccess={handleAccionExitosa}
          deuda={deuda}
          level="nested"
        />
      )}

      <AnularConMotivoDialog
        isOpen={anulandoDeuda}
        onClose={() => setAnulandoDeuda(false)}
        onConfirm={handleAnularDeuda}
        titulo="Anular deuda"
        mensaje={`Esta deuda con ${deuda.acreedor} quedará anulada. Los egresos ya registrados en el balance no se modifican.`}
        confirmText="Anular deuda"
      />

      <AnularConMotivoDialog
        isOpen={pagoAnulando !== null}
        onClose={() => setPagoAnulando(null)}
        onConfirm={handleAnularPago}
        titulo="Anular pago"
        mensaje={pagoAnulando ? `Se anulará el pago de ${formatCurrency(pagoAnulando.monto)} de la cuota ${pagoAnulando.numero_cuota}.` : ''}
        confirmText="Anular pago"
      />
    </>
  );
};
